<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="$emit('close')">
    <div class="w-full max-w-md bg-surface dark:bg-surface-dark rounded-xl shadow-xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <h2 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">Mark Time Off</h2>
        <button @click="$emit('close')" class="text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="px-6 py-4 space-y-4 overflow-y-auto">
        <template v-if="!result">
          <p class="text-sm text-fg-muted dark:text-fg-muted-dark">
            Mark a day or partial day off. Your lessons in that window will be marked excused
            and affected students will be notified.
          </p>

          <div class="flex items-center gap-2">
            <input
              id="tc-full-day"
              type="checkbox"
              v-model="fullDay"
              class="rounded border-stroke-strong dark:border-stroke-strong-dark text-blue-600 focus:ring-blue-500"
            />
            <label for="tc-full-day" class="text-sm text-fg-secondary dark:text-fg-secondary-dark select-none">Full day(s) off</label>
          </div>

          <template v-if="fullDay">
            <div class="flex items-center gap-2">
              <label class="text-xs text-fg-muted dark:text-fg-muted-dark w-10">From</label>
              <input v-model="fromDate" type="date" class="flex-1 px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-xs text-fg-muted dark:text-fg-muted-dark w-10">To</label>
              <input v-model="toDate" type="date" class="flex-1 px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </template>
          <template v-else>
            <div class="flex items-center gap-2">
              <label class="text-xs text-fg-muted dark:text-fg-muted-dark w-10">Date</label>
              <input v-model="fromDate" type="date" class="flex-1 px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-xs text-fg-muted dark:text-fg-muted-dark w-10">From</label>
              <input v-model="fromTime" type="time" class="flex-1 px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <label class="text-xs text-fg-muted dark:text-fg-muted-dark w-6 text-center">to</label>
              <input v-model="toTime" type="time" class="flex-1 px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </template>

          <div>
            <label class="block text-xs font-medium text-fg-secondary dark:text-fg-muted-dark mb-1">Reason <span class="text-fg-faint font-normal">(optional)</span></label>
            <textarea
              v-model="reason"
              rows="2"
              placeholder="e.g. out sick, family trip"
              class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>

          <p v-if="submitError" class="text-sm text-red-600 dark:text-red-400">{{ submitError }}</p>
        </template>

        <div v-else class="text-sm text-fg-base dark:text-fg-base-dark space-y-1">
          <p class="font-medium text-green-600 dark:text-green-400">Done.</p>
          <p>{{ result.affected_count }} lesson{{ result.affected_count !== 1 ? 's' : '' }} marked excused and notified.</p>
          <p v-if="result.skipped_count" class="text-fg-muted dark:text-fg-muted-dark">
            {{ result.skipped_count }} lesson{{ result.skipped_count !== 1 ? 's' : '' }} already recorded were left untouched.
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-3 px-6 py-4 border-t border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <button
          v-if="!result"
          type="button"
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark rounded-lg transition-colors"
        >Cancel</button>
        <button
          v-if="!result"
          type="button"
          @click="submit"
          :disabled="submitting || !canSubmit"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
        >{{ submitting ? 'Saving…' : 'Mark Time Off' }}</button>
        <button
          v-else
          type="button"
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'

const props = defineProps({
  initialDate: { type: String, default: null },  // 'YYYY-MM-DD', from clicking a specific calendar slot
  initialTime: { type: String, default: null },  // 'HH:MM' 24h, ditto — presence implies a partial-day window, not full day(s)
  teacherName: { type: String, default: null },  // admin marking a specific teacher's own column off, from the calendar
})
const emit = defineEmits(['close'])
const { createTeacherCancellation } = useApi()

const today = new Date().toISOString().slice(0, 10)

// A click-through from the calendar (initialTime set) always means "this one slot",
// so it starts in partial-day mode pre-filled to a half-hour window at the tapped time
// — the modal's own fullDay checkbox still lets the user widen it from there.
const _addMinutes = (hhmm, mins) => {
  const [h, m] = hhmm.split(':').map(Number)
  const total = Math.min(23 * 60 + 59, h * 60 + m + mins)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

const fullDay = ref(!props.initialTime)
const fromDate = ref(props.initialDate || today)
const toDate = ref(props.initialDate || today)
const fromTime = ref(props.initialTime || '09:00')
const toTime = ref(props.initialTime ? _addMinutes(props.initialTime, 30) : '17:00')
const reason = ref('')

const submitting = ref(false)
const submitError = ref('')
const result = ref(null)

const canSubmit = computed(() => {
  if (fullDay.value) return !!fromDate.value && !!toDate.value && toDate.value >= fromDate.value
  return !!fromDate.value && !!fromTime.value && !!toTime.value && toTime.value > fromTime.value
})

const submit = async () => {
  submitError.value = ''
  if (!canSubmit.value) {
    submitError.value = 'Please check the dates/times — the range looks invalid.'
    return
  }
  submitting.value = true
  try {
    const start_datetime = fullDay.value ? `${fromDate.value}T00:00:00` : `${fromDate.value}T${fromTime.value}:00`
    const end_datetime = fullDay.value ? `${toDate.value}T23:59:59` : `${fromDate.value}T${toTime.value}:00`
    result.value = await createTeacherCancellation({
      start_datetime,
      end_datetime,
      reason: reason.value || null,
    }, props.teacherName)
  } catch (e) {
    submitError.value = e.detail || 'Something went wrong.'
  } finally {
    submitting.value = false
  }
}
</script>
